import { useState, useEffect, useRef, useCallback } from 'react';
import { money } from '../util';
import { useAuth } from '../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { usePlan, habilitada, EtiquetaPlan, PlanBloqueado } from '../../components/Plan';

// CATEGORÍAS DE LOS PAGOS DE LA NOCHE. Lista fija y corta a propósito: la gracia es
// poder comparar noche contra noche, y con categorías libres cada noche termina con
// nombres distintos para lo mismo ("DJ", "dj", "musica") y no se puede sumar nada.
// Los pagos cargados antes de esto no tienen categoría: cuentan como "Otros".
export const CATEGORIAS_PAGO = ['Personal', 'Producción', 'Bebidas', 'Alimentos', 'Servicios', 'Otros'];
const catDe = (l) => (CATEGORIAS_PAGO.includes(l?.cat) ? l.cat : 'Otros');

export default function TabAdministracion({ event, api }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [admin, setAdmin] = useState(null);
  const [fin, setFin] = useState(null);
  const [orgName, setOrgName] = useState('el venue');
  const [renditions, setRenditions] = useState([]); // estado de rendiciones RRPP (comprobante/confirmación)
  const [subtab, setSubtab] = useState('rendiciones');
  // Fuera de Full, de esta pestaña sólo queda la rendición de los RRPP: es parte de vender
  // por RRPP (Básico). El servidor rechaza guardar el resto; acá se muestra por qué.
  const plan = usePlan();
  const completa = habilitada(plan, 'administracion');
  const [, setParams] = useSearchParams();
  useEffect(() => { if (!completa) setSubtab(s => (s === 'rendiciones' ? 'rrpp' : s)); }, [completa]);
  const [showResumen, setShowResumen] = useState(false);
  const saveTimer = useRef(null);

  const loadFin = useCallback(() => {
    api.get(`/api/admin/event/${event.id}/finance`).then(setFin).catch(() => {});
  }, [event.id]); // eslint-disable-line

  useEffect(() => {
    api.get(`/api/admin/event/${event.id}`).then(setAdmin).catch(() => {});
    loadFin();
    api.get(`/api/rrpp-renditions?event_id=${event.id}`).then(r => setRenditions(Array.isArray(r) ? r : [])).catch(() => {});
    api.get('/api/org').then(o => setOrgName(o.name || 'el venue')).catch(() => {});
  }, []); // eslint-disable-line

  // guarda un campo y refresca el cálculo (debounce)
  const save = useCallback((patch) => {
    setAdmin(a => ({ ...a, ...patch }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.patch(`/api/admin/event/${event.id}`, patch); loadFin(); } catch (e) { /* noop */ }
    }, 500);
  }, [event.id, loadFin]); // eslint-disable-line

  const loadRenditions = () => api.get(`/api/rrpp-renditions?event_id=${event.id}`).then(r => setRenditions(Array.isArray(r) ? r : [])).catch(() => {});
  // Días que le quedan al archivo antes de borrarse (se conservan 15 desde la subida).
  // Solo se avisa en las rendiciones SIN confirmar: si ya la confirmaste, el archivo
  // cumplió su función y que se borre es lo esperado.
  const DIAS_COMPROBANTE = 15;
  function diasParaBorrar(rd) {
    if (!rd || !rd.has_comprobante || !rd.uploaded_at) return null;
    const dias = (Date.now() - new Date(rd.uploaded_at).getTime()) / 86400000;
    return Math.max(0, Math.ceil(DIAS_COMPROBANTE - dias));
  }

  async function verComprobante(id) {
    try {
      const d = await api.get(`/api/rrpp-renditions/${id}/comprobante`);
      const w = window.open('', '_blank'); if (!w) { alert('Habilitá las ventanas emergentes para ver el comprobante.'); return; }
      const isPdf = /^data:application\/pdf/.test(d.comprobante);
      w.document.write(isPdf
        ? `<html><head><title>Comprobante</title></head><body style="margin:0"><embed src="${d.comprobante}" type="application/pdf" style="width:100vw;height:100vh"/></body></html>`
        : `<html><head><title>Comprobante</title></head><body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${d.comprobante}" style="max-width:100%;max-height:100vh"/></body></html>`);
      w.document.close();
    } catch (e) { alert(e.message || 'No se pudo abrir el comprobante'); }
  }
  async function confirmarRendicion(id) {
    if (!window.confirm('¿Confirmar esta rendición como cobrada? Verificá el comprobante antes.')) return;
    try { await api.post(`/api/rrpp-renditions/${id}/confirm`, {}); await loadRenditions(); } catch (e) { alert(e.message); }
  }
  const REND_ST = { pendiente: { l: 'Pendiente', c: 'var(--ink-700)' }, en_revision: { l: 'En revisión', c: '#f1c40f' }, confirmada: { l: 'Confirmada', c: '#2ecc71' } };

  if (!admin || !fin) return <div style={{ paddingTop: 30, color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>;

  const manual = Array.isArray(admin.manual_incomes) ? admin.manual_incomes : [];
  const pagos = Array.isArray(admin.payments) ? admin.payments : [];
  const settle = admin.rrpp_settle || {};
  const rendById = {}; renditions.forEach(r => { rendById[r.rrpp_id] = r; });
  const tpvSplit = admin.tpv_split || {};
  const people = fin.people || { online: 0, rrpp: 0, boleteria: 0, puerta: 0, total: 0, ingresaron: 0 };
  const t = fin.totals;

  // Consumo = solo cajas de BARRA (guardarropa y boletería no son consumo de barra).
  const consumoTotal = (fin.boxes || []).filter(b => (b.menuType || 'barra') === 'barra').reduce((a, b) => a + (Number(b.total) || 0), 0);
  // Promedio por persona = consumo de barras ÷ personas que INGRESARON (escaneados), más real.
  const ingresaron = people.ingresaron || 0;
  const consumoProm = ingresaron > 0 ? consumoTotal / ingresaron : 0;

  return (
    <div className="lb-fade" style={{ paddingTop: 24 }}>
      {/* sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[['rrpp', 'Rendición RRPP'], ['rendiciones', 'Ingresos / Egresos'], ['resumen', 'Resumen y balance']].map(([id, lbl]) => (
          <button key={id} onClick={() => setSubtab(id)} className={'lb-btn lb-btn--sm ' + (subtab === id ? 'lb-btn--white' : 'lb-btn--ghost')}>{lbl}{id !== 'rrpp' && <EtiquetaPlan plan={plan} funcion="administracion" />}</button>
        ))}
      </div>

      {subtab === 'rrpp' && (
      <div style={{ maxWidth: 920 }}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Rendición RRPP · {people.rrpp} {people.rrpp === 1 ? 'persona' : 'personas'}</div>
        <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
          <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.3fr 0.8fr 0.8fr 0.8fr 0.9fr 1fr auto' }}>
            <span>Vendedor</span><span>A rendir</span><span>Efectivo</span><span>Digital</span><span>Estado</span><span>Comprobante</span><span></span>
          </div>
          {fin.rrpps.map(r => {
            const st = settle[r.rrppId] || { efectivo: 0, digital: 0 };
            const upd = (patch) => save({ rrpp_settle: { ...settle, [r.rrppId]: { ...st, ...patch } } });
            const rd = rendById[r.rrppId] || { status: 'pendiente' };
            const ss = REND_ST[rd.status] || REND_ST.pendiente;
            return (
              <div key={r.rrppId} className="lb-row" style={{ gridTemplateColumns: '1.3fr 0.8fr 0.8fr 0.8fr 0.9fr 1fr auto', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                  {r.falta > 0 && <div style={{ fontSize: 11, color: 'var(--red-400)' }}>Falta {money(r.falta)}</div>}
                </div>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{money(r.owed)}</span>
                <MoneyInput value={st.efectivo} onChange={v => upd({ efectivo: v })} />
                <MoneyInput value={st.digital} onChange={v => upd({ digital: v })} />
                <span style={{ fontSize: 11.5, fontWeight: 700, color: ss.c }}>{ss.l}</span>
                {/* Tres estados distintos, y la diferencia importa: "sin subir" es el
                    RRPP que no rindió, "borrado" es el que SÍ rindió y el archivo ya se
                    fue por antigüedad. Mostrar lo mismo en los dos casos sería acusarlo. */}
                <span>{rd.has_comprobante
                  ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => verComprobante(rd.rendition_id)}>Ver {rd.comprobante_tipo === 'application/pdf' ? 'PDF' : 'imagen'}</button>
                      {diasParaBorrar(rd) != null && rd.status !== 'confirmada' && (
                        <span style={{ fontSize: 10.5, color: diasParaBorrar(rd) <= 3 ? 'var(--red-400)' : 'var(--ink-600)' }}>
                          {diasParaBorrar(rd) <= 0 ? 'se borra hoy' : `se borra en ${diasParaBorrar(rd)} d`}
                        </span>
                      )}
                    </span>
                  )
                  : rd.comprobante_purgado_at
                    ? <span style={{ fontSize: 11.5, color: 'var(--ink-700)' }}>rindió · archivo borrado{rd.uploaded_at ? ` (subido ${new Date(rd.uploaded_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })})` : ''}</span>
                    : <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>— sin subir —</span>}</span>
                <span style={{ textAlign: 'right' }}>
                  {rd.status === 'en_revision' && <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => confirmarRendicion(rd.rendition_id)}>Confirmar</button>}
                  {rd.status === 'confirmada' && <span style={{ fontSize: 12, color: '#2ecc71' }}>✓ Cobrada</span>}
                </span>
              </div>
            );
          })}
          {fin.rrpps.length === 0 && <div style={{ padding: '14px 0', color: 'var(--ink-600)', fontSize: 13 }}>Sin ventas de RRPP.</div>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 10, lineHeight: 1.5 }}>El monto es lo <b>a rendir</b> (lo generado − comisión). El RRPP sube el comprobante desde su panel; <b>solo vos confirmás</b> la rendición. El comprobante tiene datos bancarios: solo el dueño/admin de este boliche puede verlo.</div>
      </div>
      )}

      {subtab !== 'rrpp' && !completa && <PlanBloqueado plan={plan} funcion="administracion" onVerPlanes={() => setParams({ s: 'miplan' })} />}

      {subtab === 'rendiciones' && completa && (
      <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        {/* IZQUIERDA: ingresos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* 1 · Venta online */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Venta online (digital, automático)</div>
            <div className="lb-card" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: 'var(--ink-700)', fontSize: 13 }}>Entradas vendidas online</div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{people.online} {people.online === 1 ? 'persona' : 'personas'}</div>
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: '#fff' }}>{money(fin.ventaOnline)}</span>
            </div>
          </div>

          {/* 2 · Venta RRPP — resumen (el detalle y la confirmación están en la sub-pestaña "Rendición RRPP") */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Venta RRPP · {people.rrpp} {people.rrpp === 1 ? 'persona' : 'personas'}</div>
            <div className="lb-card" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: 'var(--ink-700)', fontSize: 13 }}>Total a rendir</div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>Detalle y confirmación en la pestaña “Rendición RRPP”.</div>
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: '#fff' }}>{money(fin.rrpps.reduce((a, r) => a + Number(r.owed || 0), 0))}</span>
            </div>
          </div>

          {/* 3 · Boletería (automático) */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Boletería (automático)</div>
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 0.6fr 1fr' }}>
                <span>Lote</span><span style={{ textAlign: 'center' }}>Cant.</span><span style={{ textAlign: 'right' }}>Monto</span>
              </div>
              {(fin.boleteria?.lots || []).map(l => (
                <div key={l.lotId} className="lb-row" style={{ gridTemplateColumns: '1.6fr 0.6fr 1fr' }}>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', fontFamily: 'var(--font-display)' }}>{l.name}</span>
                  <span style={{ textAlign: 'center', color: 'var(--ink-800)' }}>{l.qty}</span>
                  <span style={{ textAlign: 'right', color: '#fff', fontWeight: 600 }}>{money(l.monto)}</span>
                </div>
              ))}
              {!(fin.boleteria?.count > 0) && <div style={{ padding: '14px 0', color: 'var(--ink-600)', fontSize: 13 }}>Sin ventas de boletería en este evento.</div>}
              {fin.boleteria?.count > 0 && (
                <div className="lb-row" style={{ gridTemplateColumns: '1.6fr 0.6fr 1fr', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>Total · {fin.boleteria.count} {fin.boleteria.count === 1 ? 'entrada' : 'entradas'}</span>
                  <span />
                  <span style={{ textAlign: 'right', color: '#fff', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{money(fin.boleteria.total)}</span>
                </div>
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-600)' }}>La plata de boletería se rinde en “Rendición de cajas TPV” (la caja de boletería aparece abajo con su total).</div>
          </div>

          {/* Total de personas en la noche */}
          <div className="lb-card" style={{ padding: '18px 22px', background: 'var(--ink-050)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div className="lb-eyebrow">Personas · venta total</div>
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Online {people.online} · RRPP {people.rrpp} · Boletería {people.boleteria || 0}{people.puerta ? ` · Puerta ${people.puerta}` : ''} · Ingresaron {people.ingresaron || 0}</div>
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, color: '#fff' }}>{people.total}</span>
          </div>

          {/* 4 · Rendición TPV */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Rendición de cajas TPV</div>
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr' }}>
                <span>Caja</span><span>Efectivo</span><span>Digital</span><span>Total caja</span>
              </div>
              {fin.boxes.map(b => {
                const st = tpvSplit[b.boxId] || { efectivo: 0, digital: 0 };
                const upd = (patch) => save({ tpv_split: { ...tpvSplit, [b.boxId]: { ...st, ...patch } } });
                return (
                  <div key={b.boxId} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr' }}>
                    <div>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                        {b.name}{b.kind === 'moza' && <span style={{ fontSize: 10.5, color: 'var(--ink-600)', fontWeight: 500 }}> · moza</span>}
                      </div>
                      {/* Si parte se pagó con el saldo de una mesa, esa plata no la tiene la
                          caja: se aclara acá o el número de abajo parece un faltante. */}
                      {b.conSaldo > 0 && <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{money(b.conSaldo)} con saldo de mesa · rinde {money(b.aRendir)}</div>}
                      {Math.abs(b.falta) > 0 && <div style={{ fontSize: 11, color: b.falta > 0 ? 'var(--red-400)' : 'var(--ink-600)' }}>{b.falta > 0 ? `Falta ${money(b.falta)}` : `+${money(-b.falta)} de más`}</div>}
                    </div>
                    <MoneyInput value={st.efectivo} onChange={v => upd({ efectivo: v })} />
                    <MoneyInput value={st.digital} onChange={v => upd({ digital: v })} />
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{money(b.total)}</span>
                  </div>
                );
              })}
              {fin.boxes.length === 0 && <div style={{ padding: '14px 0', color: 'var(--ink-600)', fontSize: 13 }}>Sin cajas TPV.</div>}
            </div>
          </div>

          {/* Ingresos manuales */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Ingresos manuales</div>
            <SplitLines lines={manual} onChange={v => save({ manual_incomes: v })} placeholder="Concepto" />
          </div>
        </div>

        {/* DERECHA: pagos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Pagos de la noche</div>
            <SplitLines lines={pagos} onChange={v => save({ payments: v })} placeholder="DJ / seguridad / sonido…" redAmount conCategoria />
          </div>
        </div>
      </div>
      )}

      {subtab === 'resumen' && completa && (
      <div>
        {!showResumen ? (
          <div className="lb-card" style={{ padding: '28px 26px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 30, color: 'var(--ink-600)', letterSpacing: '0.1em' }}>$ •••••••</div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 6, maxWidth: 440 }}>Resumen y balance ocultos para no exponer la ganancia mientras el personal rinde.</div>
            </div>
            {isOwner && <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setShowResumen(true)}>Ver resumen</button>}
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {/* RESUMEN DE LA NOCHE */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Resumen de la noche</div>
            <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <SummaryCard label="Venta total · personas" value={people.total} sub={`Online ${people.online} · RRPP ${people.rrpp} · Boletería ${people.boleteria || 0}${people.puerta ? ` · Puerta ${people.puerta}` : ''}`} />
              <SummaryCard label="Cantidad que ingresaron" value={people.ingresaron || 0} sub={ingresoSub(people)} />
              <SummaryCard label="Consumo total de barras" value={money(consumoTotal)} sub="Solo cajas de barra (sin guardarropa ni boletería)" />
              <SummaryCard label="Consumo promedio por persona" value={money(Math.round(consumoProm))} sub={ingresaron > 0 ? `${money(consumoTotal)} ÷ ${ingresaron} que ingresaron` : 'Sin ingresos escaneados todavía'} />
              <SummaryCard label="Ganancia de la noche" value={money(t.ganancia)} sub={t.ganancia >= 0 ? 'Positivo' : 'Negativo'} accent={t.ganancia >= 0 ? '#2ecc71' : 'var(--red-400)'} />
            </div>
          </div>

          {/* BALANCE */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Balance de la noche</div>
            <div className="lb-card" style={{ padding: '28px 26px' }}>
            {/* dos totales */}
            <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 24 }}>
              <div className="lb-card" style={{ padding: '18px 20px', background: 'var(--ink-050)' }}>
                <div className="lb-eyebrow">Efectivo en caja</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: t.efectivo >= 0 ? '#fff' : 'var(--red-400)', marginTop: 6 }}>{money(t.efectivo)}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Ingresos {money(t.ingEf)} − pagos {money(t.pagosEf)}</div>
              </div>
              <div className="lb-card" style={{ padding: '18px 20px', background: 'var(--ink-050)' }}>
                <div className="lb-eyebrow">En cuenta / digital</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: t.digital >= 0 ? '#fff' : 'var(--red-400)', marginTop: 6 }}>{money(t.digital)}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Ingresos {money(t.ingDig)} − pagos {money(t.pagosDig)}</div>
              </div>
            </div>

            {/* desglose */}
            <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
              <div>
                <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Ingresos</div>
                <Line label="Venta online" ef={0} dig={fin.ventaOnline} />
                {fin.serviceFeeOnline > 0 && <Line label="Cargos por servicio · online" ef={0} dig={fin.serviceFeeOnline} />}
                <Line label="Rendición RRPP" ef={t.rrppEf} dig={t.rrppDig} />
                {(t.doorEf !== 0 || t.doorDig !== 0) && <Line label="Venta en puerta" ef={t.doorEf} dig={t.doorDig} />}
                <Line label="Cajas TPV" ef={t.tpvEf} dig={t.tpvDig} />
                <Line label="Manuales" ef={t.manualEf} dig={t.manualDig} />
                <Line label="Total ingresos" ef={t.ingEf} dig={t.ingDig} strong />
              </div>
              <div>
                <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Egresos</div>
                <Line label="Pagos" ef={t.pagosEf} dig={t.pagosDig} neg strong />
              </div>
            </div>

            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="lb-eyebrow">Ganancia</span>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 30, color: t.ganancia >= 0 ? '#2ecc71' : 'var(--red-400)' }}>{money(t.ganancia)}</span>
                <span className={'lb-status ' + (t.ganancia >= 0 ? 'lb-status--paid' : 'lb-status--used')}>{t.ganancia >= 0 ? 'Positivo' : 'Negativo'}</span>
              </div>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setShowResumen(false)}>Ocultar resumen</button>
            </div>

            <CostoDeAbrir pagos={pagos} totals={t} consumoBarra={consumoTotal} />

            {/* Lo que queda a LOBBO */}
            <CutEditor orgName={orgName} cut={admin.cut || { type: 'pct', value: 100 }} totals={t} onChange={cut => save({ cut })} />
            </div>
          </div>
        </div>
        )}
        <EventNotes event={event} api={api} />
      </div>
      )}
    </div>
  );
}

// Nota / conclusión del evento (privada, owner/admin). Se guarda sola. Antes era una pestaña
// aparte; ahora vive al pie de "Resumen y balance" como conclusión de la noche.
function EventNotes({ event, api }) {
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('');
  const t = useRef(null);
  useEffect(() => { api.get(`/api/admin/notes/${event.id}`).then(n => setContent(n.content || '')).catch(() => {}); }, [event.id]); // eslint-disable-line
  const onChange = (v) => {
    setContent(v); setStatus('Guardando…');
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try { await api.patch(`/api/admin/notes/${event.id}`, { content: v }); setStatus('Se guarda solo ✓'); }
      catch { setStatus('Error al guardar'); }
    }, 700);
  };
  return (
    <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="lb-eyebrow">Conclusión de la noche (privada)</div>
        <span style={{ fontSize: 12, color: status.includes('✓') ? '#2ecc71' : 'var(--ink-600)' }}>{status || 'Se guarda solo'}</span>
      </div>
      <textarea className="lb-input" style={{ minHeight: 160, fontSize: 14.5 }} value={content} onChange={e => onChange(e.target.value)}
        placeholder="Anotaciones de esta noche para tener en cuenta a futuro: qué funcionó, incidentes, proveedores, pendientes… Solo lo ven el dueño y los administradores." />
    </div>
  );
}

function CutEditor({ orgName, cut, totals, onChange }) {
  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Lo que queda a {orgName} → ingreso mensual</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button className={'lb-btn lb-btn--sm ' + (cut.type === 'pct' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => onChange({ type: 'pct', value: cut.type === 'pct' ? cut.value : 100 })}>Porcentaje</button>
        <button className={'lb-btn lb-btn--sm ' + (cut.type === 'fixed' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => onChange({ type: 'fixed', value: cut.type === 'fixed' ? cut.value : 0 })}>Monto fijo</button>
      </div>
      {cut.type === 'pct' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input className="lb-input" style={{ width: 100 }} inputMode="numeric" value={cut.value} onChange={e => onChange({ type: 'pct', value: +e.target.value.replace(/\D/g, '') || 0 })} />
          <span style={{ color: 'var(--ink-700)' }}>% de la ganancia</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--ink-700)' }}>$</span>
          <input className="lb-input" style={{ width: 160 }} inputMode="numeric" value={cut.value} onChange={e => onChange({ type: 'fixed', value: +e.target.value.replace(/\D/g, '') || 0 })} />
          <span style={{ color: 'var(--ink-700)' }}>monto fijo</span>
        </div>
      )}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <span className="lb-eyebrow">Queda a {orgName}</span>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: 'var(--red-400)' }}>{money(totals.lobbo)}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>(efectivo {money(totals.lobboEf)} · digital {money(totals.lobboDig)})</span>
      </div>
    </div>
  );
}

// COSTO DE ABRIR LA NOCHE: qué parte de lo que se pagó se fue en cada cosa.
//
// Los dos porcentajes de abajo son los que se miran de verdad: lo que cuesta abrir
// contra lo que entró (si se acerca al 100%, la noche se trabajó para pagar costos), y
// lo que se pagó de bebida contra lo que vendieron las barras — el margen de la barra,
// que es donde se gana. Ojo: la bebida se paga cuando llega el pedido, no cada noche,
// así que ese número se lee por mes más que por fiesta.
function CostoDeAbrir({ pagos, totals, consumoBarra }) {
  const lineas = Array.isArray(pagos) ? pagos : [];
  const total = lineas.reduce((a, l) => a + (+l.efectivo || 0) + (+l.digital || 0), 0);
  const porCat = CATEGORIAS_PAGO.map(c => ({
    cat: c,
    monto: lineas.filter(l => catDe(l) === c).reduce((a, l) => a + (+l.efectivo || 0) + (+l.digital || 0), 0),
  })).filter(x => x.monto > 0).sort((a, b) => b.monto - a.monto);
  const ventaTotal = (totals.ingEf || 0) + (totals.ingDig || 0);
  const bebidas = porCat.find(x => x.cat === 'Bebidas')?.monto || 0;
  const pct = (n, sobre) => (sobre > 0 ? Math.round((n / sobre) * 100) : 0);

  return (
    <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Costo de abrir la noche</div>
      {total === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-600)' }}>Todavía no cargaste pagos de esta noche.</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--red-400)' }}>{money(total)}</span>
            {ventaTotal > 0 && <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>{pct(total, ventaTotal)}% de la venta total ({money(ventaTotal)})</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {porCat.map(({ cat, monto }) => (
              <div key={cat}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{cat}</span>
                  <span style={{ color: 'var(--ink-700)' }}>{money(monto)} · {pct(monto, total)}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--ink-050)', marginTop: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${pct(monto, total)}%`, height: '100%', background: 'var(--red-400)' }} />
                </div>
              </div>
            ))}
          </div>
          {bebidas > 0 && consumoBarra > 0 && (
            <div style={{ marginTop: 18, fontSize: 12, color: 'var(--ink-600)' }}>
              Bebidas {money(bebidas)} sobre {money(consumoBarra)} de consumo en barras: <strong style={{ color: '#fff' }}>{pct(bebidas, consumoBarra)}%</strong> de lo que vendieron las barras.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SplitLines({ lines, onChange, placeholder, redAmount, conCategoria }) {
  const [nc, setNc] = useState(''); const [nef, setNef] = useState(''); const [nd, setNd] = useState('');
  // La categoría del renglón nuevo arranca en la última usada: una noche se cargan
  // varios pagos seguidos del mismo tipo (tres personas de personal, dos proveedores).
  const [ncat, setNcat] = useState('Personal');
  const setLine = (i, k, v) => onChange(lines.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const rm = (i) => onChange(lines.filter((_, j) => j !== i));
  const add = () => {
    if (!nc.trim() && !nef && !nd) return;
    const linea = { concepto: nc || placeholder, efectivo: +nef || 0, digital: +nd || 0 };
    if (conCategoria) linea.cat = ncat;
    onChange([...lines, linea]); setNc(''); setNef(''); setNd('');
  };
  const col = redAmount ? 'var(--red-400)' : '#fff';
  const cols = conCategoria ? '1.5fr 1.3fr 0.95fr 0.95fr 0.85fr 28px' : '1.3fr 1fr 1fr 0.8fr 28px';
  const minW = conCategoria ? 610 : undefined;
  return (
    <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
      <div style={{ minWidth: minW }}>
      <div className="lb-row lb-th" style={{ gridTemplateColumns: cols }}>
        <span>Concepto</span>{conCategoria && <span>Categoría</span>}<span>Efectivo</span><span>Digital</span><span>Total</span><span></span>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="lb-row" style={{ gridTemplateColumns: cols }}>
          <input className="lb-input ev-edit" value={l.concepto} onChange={e => setLine(i, 'concepto', e.target.value)} />
          {conCategoria && (
            <select className="lb-input ev-edit" value={catDe(l)} onChange={e => setLine(i, 'cat', e.target.value)}>
              {CATEGORIAS_PAGO.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <input className="lb-input ev-edit" inputMode="numeric" value={l.efectivo} onChange={e => setLine(i, 'efectivo', +e.target.value.replace(/\D/g, '') || 0)} style={{ color: col }} />
          <input className="lb-input ev-edit" inputMode="numeric" value={l.digital} onChange={e => setLine(i, 'digital', +e.target.value.replace(/\D/g, '') || 0)} style={{ color: col }} />
          <span style={{ color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13 }}>{money((+l.efectivo || 0) + (+l.digital || 0))}</span>
          <button className="lb-iconbtn" onClick={() => rm(i)}>✕</button>
        </div>
      ))}
      <div className="lb-row" style={{ gridTemplateColumns: cols, borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
        <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder={placeholder} value={nc} onChange={e => setNc(e.target.value)} />
        {conCategoria && (
          <select className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} value={ncat} onChange={e => setNcat(e.target.value)}>
            {CATEGORIAS_PAGO.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Efectivo" inputMode="numeric" value={nef} onChange={e => setNef(e.target.value.replace(/\D/g, ''))} />
        <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Digital" inputMode="numeric" value={nd} onChange={e => setNd(e.target.value.replace(/\D/g, ''))} />
        <span style={{ color: 'var(--ink-700)', fontSize: 13 }}>{money((+nef || 0) + (+nd || 0))}</span>
        <button className="lb-btn lb-btn--white lb-btn--sm" onClick={add}>+</button>
      </div>
      </div>
    </div>
  );
}

// Detalle de la tarjeta "cantidad que ingresaron": el desglose por canal contra lo
// vendido de cada uno. Un canal con mucho vendido y poco escaneado significa que en la
// puerta no le están pasando los QR — el total solo no lo muestra.
function ingresoSub(p) {
  const ic = p.ingresaronPorCanal;
  if (!ic) return 'Entradas escaneadas en la puerta';
  const partes = [
    ['Online', ic.online, p.online],
    ['RRPP', ic.rrpp, p.rrpp],
    ['Boletería', ic.boleteria, p.boleteria],
    ['Puerta', ic.puerta, p.puerta],
  ].filter(([, , vend]) => vend > 0).map(([n, ing, vend]) => `${n} ${ing}/${vend}`);
  return partes.length ? `Escaneadas en la puerta · ${partes.join(' · ')}` : 'Entradas escaneadas en la puerta';
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className="lb-card" style={{ padding: '20px 22px', background: 'var(--ink-050)' }}>
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, color: accent || '#fff', marginTop: 8 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function MoneyInput({ value, onChange }) {
  return <input className="lb-input ev-edit" inputMode="numeric" value={value || ''} placeholder="0"
    onChange={e => onChange(+e.target.value.replace(/\D/g, '') || 0)} />;
}

function Line({ label, ef, dig, neg, strong }) {
  const tot = (ef || 0) + (dig || 0);
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <span style={{ fontSize: 13, color: strong ? '#fff' : 'var(--ink-700)', fontWeight: strong ? 600 : 400 }}>{label}</span>
      <span style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
        <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>ef {money(ef)} · dig {money(dig)}</span>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: strong ? 16 : 14, color: neg ? 'var(--red-400)' : '#fff', minWidth: 80, textAlign: 'right' }}>{neg ? '− ' : ''}{money(tot)}</span>
      </span>
    </div>
  );
}
